import React, { useContext, useEffect, useState } from "react";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import clsx from "clsx";

import {
  ListInstallationsResponse,
  ListInstallationsResponseItem,
} from "@safetyculture/s12-apis-connect-web/s12/integrations/admin/v1/integrations_admin_pb";

import mainStyles from "../../../styles";
import DisplayHeading from "../../shared/display-heading";
import {
  deleteIntegrations,
  listIntegrations,
} from "api/endpoints/integrations";
import { callOptionsHeaders } from "api/helpers";
import { SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";

interface IntegrationsTabProps {
  regionCode: string;
  roleId: string;
}

function IntegrationsTab({ regionCode, roleId }: IntegrationsTabProps) {
  const theme = useTheme();
  const classes = mainStyles();
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const { showError } = useContext(SnackbarContext);
  const [showDialog, setShowDialog] = useState(false);
  const [showCfgDialog, setShowCfgDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(
    {} as ListInstallationsResponseItem | null
  );
  const handleClickOpen = (record: ListInstallationsResponseItem) => {
    setSelectedRecord(record);
    setShowDialog(true);
  };
  const handleClose = () => {
    setSelectedRecord(null);
    setShowDialog(false);
  };
  const handleCfgClickOpen = (record: ListInstallationsResponseItem) => {
    setSelectedRecord(record);
    setShowCfgDialog(true);
  };
  const handleCfgClickClose = () => {
    setSelectedRecord(null);
    setShowCfgDialog(false);
  };

  const [userInstallations, setUserInstallations] = useState(
    new ListInstallationsResponse()
  );
  const UserIntegrations = userInstallations.items;
  const integrationsList = () => {
    setShowLoadingSpinner(true);
    listIntegrations(
      { organisationID: toUUID(roleId) },
      callOptionsHeaders(regionCode, roleId)
    )
      .then((res) => {
        setUserInstallations(res);
      })
      .catch(showError)
      .finally(() => {
        setShowLoadingSpinner(false);
      });
  };

  const deleteIntegration = () => {
    if (selectedRecord == null) {
      return;
    }

    deleteIntegrations(
      { organisationID: toUUID(roleId), integrationID: selectedRecord.id },
      callOptionsHeaders(regionCode, roleId)
    )
      .then(() => {
        handleClose();
        integrationsList();
      })
      .catch(showError)
      .finally(() => {
        setShowLoadingSpinner(false);
      });
  };

  const jsonPretty = () => {
    if (selectedRecord == null) {
      return "ERROR PARSING THE CONFIGURATION";
    }

    try {
      const cfg = JSON.parse(selectedRecord.configuration);
      return JSON.stringify(cfg, null, 2);
    } catch (e) {
      return "ERROR PARSING THE CONFIGURATION: " + selectedRecord.configuration;
    }
  };

  useEffect(() => {
    integrationsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (showLoadingSpinner) {
    return (
      <Grid container>
        <Grid item xs={12} sx={classes.paddingTopSmall}>
          <DisplayHeading>Integrations</DisplayHeading>
          <Table>
            <TableRow>
              <LinearProgress />
            </TableRow>
          </Table>
        </Grid>
      </Grid>
    );
  }
  if (UserIntegrations.length === 0 && !showLoadingSpinner) {
    return (
      <Grid container>
        <Grid item xs={12} sx={classes.paddingTopSmall}>
          <DisplayHeading>Integrations</DisplayHeading>
          <TableRow>
            <TableCell colSpan={5} data-testid="no-integrations-message">
              <b>{"This organisation doesn't have any integrations"}</b>
            </TableCell>
          </TableRow>
        </Grid>
      </Grid>
    );
  }
  return (
    <Grid container>
      <Grid item xs={12} sx={classes.paddingTopSmall}>
        <DisplayHeading>Integrations</DisplayHeading>
      </Grid>
      <Grid item xs={12}>
        <Box
          style={{
            borderColor: theme.palette.divider,
            borderWidth: "1px",
            borderStyle: "solid",
            borderRadius: "5px",
            margin: "20px",
            alignItems: "center",
          }}
        >
          <TableContainer>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell
                    className={clsx(classes.tableCell, classes.leftCell)}
                    align="left"
                  >
                    Integration
                  </TableCell>
                  <TableCell
                    className={clsx(
                      classes.tableCell,
                      classes.leftCell,
                      classes.tableLeftColPadding,
                      classes.tableRightColPadding
                    )}
                    align="center"
                  >
                    State
                  </TableCell>
                  <TableCell
                    className={clsx(
                      classes.tableCell,
                      classes.leftCell,
                      classes.tableLeftColPadding,
                      classes.tableRightColPadding
                    )}
                    align="center"
                  >
                    Modify
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {UserIntegrations.map((integration) => (
                  <TableRow key={integration.id}>
                    <TableCell
                      className={clsx(classes.tableCell, classes.leftCell)}
                      align="left"
                    >
                      {integration.integrationName}
                    </TableCell>
                    <TableCell align="center">{integration.state}</TableCell>
                    <TableCell
                      className={clsx(
                        classes.tableCell,
                        classes.tableRightColPadding,
                        classes.spaceTopSmall,
                        classes.spaceBottomSmall
                      )}
                      align="center"
                    >
                      <Button
                        className={clsx(
                          classes.spaceBottomLarge,
                          classes.spaceTopLarge
                        )}
                        variant="contained"
                        color="primary"
                        onClick={() => handleCfgClickOpen(integration)}
                      >
                        Configuration
                      </Button>
                      <Button
                        className={clsx(
                          classes.spaceBottomLarge,
                          classes.spaceTopLarge,
                          classes.spaceLeft
                        )}
                        variant="contained"
                        color="secondary"
                        onClick={() => handleClickOpen(integration)}
                      >
                        Delete Integration
                      </Button>
                      <Dialog
                        open={showCfgDialog}
                        onClose={handleCfgClickClose}
                        fullWidth={true}
                        maxWidth="lg"
                      >
                        <DialogTitle>
                          <Typography variant="h6">
                            Configuration for {selectedRecord?.id}
                          </Typography>
                        </DialogTitle>
                        <DialogContent style={{ whiteSpace: "pre-wrap" }}>
                          <pre>
                            <code>{jsonPretty()}</code>
                          </pre>
                        </DialogContent>
                      </Dialog>
                      <Dialog open={showDialog} onClose={handleClose}>
                        <DialogTitle>
                          <Typography variant="h6">
                            Are you sure you want to delete this integration?
                          </Typography>
                          <Typography variant="subtitle2">
                            <p>
                              <div>
                                Integration name:{" "}
                                {selectedRecord?.integrationName}
                              </div>
                              <div>Installation id: {selectedRecord?.id}</div>
                            </p>
                            <p>
                              <div>
                                Always ask the customer to uninstall their own
                                integration.
                              </div>
                              <Typography
                                variant="subtitle2"
                                style={{ fontWeight: "bold" }}
                              >
                                This delete function only removes the record
                                from the database to fix corrupted installations
                              </Typography>
                            </p>
                          </Typography>

                          <DialogActions>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => handleClose()}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              color="secondary"
                              onClick={() => {
                                deleteIntegration();
                              }}
                            >
                              Delete
                            </Button>
                          </DialogActions>
                        </DialogTitle>
                      </Dialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Grid>
    </Grid>
  );
}

export default IntegrationsTab;
